import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx2DRuntime } from "../../vfx_2d_runtime.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { GenericSpriteBatch } from "../../core/batching/generic_sprite_batch.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { Emitter } from "../../core/emitters/emitter.js";
import { randomRange } from "../../../common/common_funcs.js";
import { ColorMode } from "../../core/types/webgl_types.js";

interface IBoosterParticle
{
  stable_id   : number;
  from        : Vector2;
  to          : Vector2;
  size        : Vector2;
  start_alpha : number;
  start_time  : number;
  duration    : number;
}

export class VfxBoosterHighlightGlowTask implements IVfxTask, IController
{
  private static readonly GLOW_COLOR       : { x: number; y: number; z: number } = { x: 1.0, y: 0.627, z: 0.156 };
  private static readonly SCALE_FACTOR     : number = 2;
  private static readonly BASE_ALPHA       : number = 0.85;
  private static readonly PULSE_BASE_ALPHA : number = 0.75;
  private static readonly PULSE_AMPLITUDE  : number = 0.25;
  private static readonly PULSE_SPEED      : number = 0.005;
  private static readonly ADDITIVE         : number = 1;

  // Particle Emitter Constants
  private static readonly PARTICLE_GLOW_COLOR      : { x: number; y: number; z: number } = { x: 1.0, y: 0.392, z: 0.156 };
  private static readonly PARTICLE_EMIT_RATE       : number = 3;    // particles per second
  private static readonly PARTICLE_EMIT_COUNT      : number = 4;    // particles per second
  private static readonly MIN_FLIGHT_DIST          : number = 120;  // min radial distance
  private static readonly MAX_FLIGHT_DIST          : number = 200;  // max radial distance
  private static readonly PARTICLE_SIZE            : number = 50;   // particle diameter
  private static readonly PARTICLE_LIFE            : number = 1800; // ms
  private static readonly PARTICLE_START_ALPHA     : number = 1;
  private static readonly PARTICLE_FADE_BREAKPOINT : number = 0.8;

  private target: Vector2;
  private glow_size: Vector2;
  private start_time: number;
  private glow_id: number = -1;
  private is_finished: boolean = false;
  private stopping: boolean = false;
  private stop_time: number = 0;
  private fade_duration: number = 300;

  private emitter: Emitter;
  private particles: IBoosterParticle[] = [];
  private readonly layer: "main_back" | "overlay_back";

  private spriteBatch(): GenericSpriteBatch
  {
    return this.layer === "overlay_back"
      ? Vfx2DRuntime.onOverlayBack( GenericSpriteBatch )
      : Vfx2DRuntime.onMainBack( GenericSpriteBatch );
  }

  constructor(
    target: Vector2,
    size: Vector2,
    start_time: number = performance.now(),
    layer: "main_back" | "overlay_back" = "main_back"
  )
  {
    this.layer = layer;
    this.target = target.clone();
    const scaled_side = Math.max( size.x, size.y ) * VfxBoosterHighlightGlowTask.SCALE_FACTOR;
    this.glow_size = new Vector2( scaled_side, scaled_side );
    this.start_time = start_time;

    const sprite_batch = this.spriteBatch();

    this.glow_id = sprite_batch.spawnSprite(
      ImgID.CircleGlow1,
      this.target,
      this.glow_size,
      VfxBoosterHighlightGlowTask.GLOW_COLOR,
      VfxBoosterHighlightGlowTask.BASE_ALPHA,
      0.0,
      VfxBoosterHighlightGlowTask.ADDITIVE,
      this.start_time,
      Number.MAX_SAFE_INTEGER,
      ColorMode.Mask,
    );

    this.emitter = new Emitter( this.target, this.start_time, undefined, {
      rate_over_time: VfxBoosterHighlightGlowTask.PARTICLE_EMIT_RATE,
      count: VfxBoosterHighlightGlowTask.PARTICLE_EMIT_COUNT,
    } );
  }

  private spawnParticle( spawn_pos: Vector2, spawn_time: number ): void
  {
    const scale = LayoutScaler.getScale();
    const angle = randomRange( 0, Math.PI * 2 );
    const dist = randomRange( VfxBoosterHighlightGlowTask.MIN_FLIGHT_DIST, VfxBoosterHighlightGlowTask.MAX_FLIGHT_DIST ) * scale;
    const diameter = VfxBoosterHighlightGlowTask.PARTICLE_SIZE * scale;
    const duration = VfxBoosterHighlightGlowTask.PARTICLE_LIFE;
    const size_vec = new Vector2( diameter, diameter );

    const from = spawn_pos.clone();
    const to = new Vector2(
      spawn_pos.x + Math.cos( angle ) * dist,
      spawn_pos.y + Math.sin( angle ) * dist
    );

    const sprite_batch = this.spriteBatch();

    const stable_id = sprite_batch.spawnSprite(
      ImgID.CircleGlow4,
      from,
      size_vec,
      VfxBoosterHighlightGlowTask.PARTICLE_GLOW_COLOR,
      VfxBoosterHighlightGlowTask.PARTICLE_START_ALPHA,
      0.0,
      VfxBoosterHighlightGlowTask.ADDITIVE,
      spawn_time,
      duration,
      ColorMode.Mask,
    );

    if ( stable_id !== -1 )
    {
      this.particles.push( {
        stable_id,
        from,
        to,
        size: size_vec,
        start_alpha: VfxBoosterHighlightGlowTask.PARTICLE_START_ALPHA,
        start_time: spawn_time,
        duration,
      } );
    }
  }

  public setPosition( pos: Vector2 ): void
  {
    this.target.copyFrom( pos );
    this.emitter.setPosition( pos );
  }

  public accelerate( factor: number, now: number ): void {}

  public stop( behavior?: StopBehavior, fade_duration?: number ): void
  {
    if ( this.is_finished || this.stopping )
      return;

    this.stopping = true;
    this.stop_time = performance.now();
    this.fade_duration = fade_duration ?? 300;
    this.emitter.stop();
  }

  public onUpdate( now: number ): boolean
  {
    const elapsed = now - this.start_time;
    if ( elapsed < 0 )
      return false;

    const sprite_batch = this.spriteBatch();

    // 1. Оновлюємо основне пульсуюче світіння
    if ( this.glow_id !== -1 )
    {
      let alpha = VfxBoosterHighlightGlowTask.PULSE_BASE_ALPHA +
        VfxBoosterHighlightGlowTask.PULSE_AMPLITUDE * Math.sin( elapsed * VfxBoosterHighlightGlowTask.PULSE_SPEED );

      if ( this.stopping )
      {
        const stop_elapsed = now - this.stop_time;
        if ( stop_elapsed >= this.fade_duration )
        {
          sprite_batch.triggerDeath( this.glow_id, now );
          this.glow_id = -1;
        }
        else
        {
          const fade_progress = stop_elapsed / this.fade_duration;
          alpha *= ( 1.0 - fade_progress );
          sprite_batch.updateSpriteData( this.glow_id, this.target, this.glow_size, alpha, 0.0 );
        }
      }
      else
      {
        sprite_batch.updateSpriteData( this.glow_id, this.target, this.glow_size, alpha, 0.0 );
      }
    }

    // 2. Обробка емітера спавну частинок під час активного ефекту
    if ( !this.stopping )
    {
      this.emitter.setPosition( this.target );
      this.emitter.process( now, ( spawn_pos, spawn_time ) => this.spawnParticle( spawn_pos, spawn_time ) );
    }

    // 3. Оновлення та переміщення активних частинок
    for ( let i = this.particles.length - 1; i >= 0; i-- )
    {
      const p = this.particles[i];
      const p_elapsed = now - p.start_time;
      const progress = p_elapsed / p.duration;

      if ( progress >= 1.0 )
      {
        sprite_batch.updateSpriteData( p.stable_id, this.target, new Vector2( 0, 0 ), 0.0, 0.0 );
        this.particles.splice( i, 1 );
      }
      else if ( progress >= 0 )
      {
        const cur_pos = new Vector2(
          p.from.x + ( p.to.x - p.from.x ) * progress,
          p.from.y + ( p.to.y - p.from.y ) * progress
        );
        // Частинки починають втрачати прозорість після PARTICLE_FADE_BREAKPOINT (0.8) життєвого циклу
        let cur_alpha = p.start_alpha;
        if ( progress > VfxBoosterHighlightGlowTask.PARTICLE_FADE_BREAKPOINT )
        {
          const fade_progress = ( progress - VfxBoosterHighlightGlowTask.PARTICLE_FADE_BREAKPOINT ) /
            ( 1.0 - VfxBoosterHighlightGlowTask.PARTICLE_FADE_BREAKPOINT );
          cur_alpha = p.start_alpha * ( 1.0 - fade_progress );
        }

        // Якщо завдання зупиняється, частинки також можуть поступово згасати разом з основним світінням
        // Або просто продовжують свій власний цикл. За умовою - "частинки просто доживали свій час".
        // Тому додаткове прозорішання від stopping не застосовуємо.

        sprite_batch.updateSpriteData( p.stable_id, cur_pos, p.size, cur_alpha, 0.0 );
      }
    }

    if ( this.stopping && this.glow_id === -1 && this.particles.length === 0 )
    {
      this.is_finished = true;
      return true;
    }

    return false;
  }
}
